/**
 * Client-only optimistic state for chat turns in flight.
 *
 * `sendMessage` has no client-supplied id to echo back (see
 * `packages/shared/src/callables.ts#SendMessageRequest/Response`) — touching
 * the contract just to add one wasn't worth it for a purely-cosmetic UI
 * affordance, so a turn is reconciled against the real Firestore doc
 * conservatively: same role + exact content, matched in submission order
 * (`classifyPendingTurns`). This holds because `sendMessage` appends the
 * user's message before doing anything else (see
 * `functions/src/callables/sendMessage.ts`), so real messages always show up
 * in the same order turns were submitted.
 *
 * Module-level (outside React) on purpose: `ChatView` is remounted when
 * navigating from `/` to `/conversations/$id` (two distinct route
 * components share it — see `chat-view.tsx`), so component-local state would
 * lose an in-flight turn exactly when a brand-new conversation is created.
 * A plain external store survives that remount; `useOptimisticTurns`
 * (`hooks/useOptimisticTurns.ts`) subscribes to it via `useSyncExternalStore`.
 */
import type { Attachment, MessageDoc } from '@agente/shared'

/** Sentinel key for a conversation that doesn't exist yet (chat home, `/`). */
export const NEW_CONVERSATION_KEY = '__new__'

/** Allowance for server/client clock skew when matching a turn to its echo. */
const CLOCK_SKEW_MS = 60_000

/**
 * Past this, an open turn ('sending' | 'awaiting-reply') is defensively
 * flipped to 'error' (see `findTimedOutTurns`) — it stops disabling the
 * composer and gets the retry/discard affordance, instead of leaving the
 * user stuck on a thinking indicator forever (e.g. a client clock far enough
 * ahead of the server's that the echo-matching window in
 * `classifyPendingTurns` never overlaps).
 */
export const THINKING_TIMEOUT_MS = 90_000

/** pt-BR shown on a turn that timed out — the send may have gone through. */
export const THINKING_TIMEOUT_MESSAGE =
  'A resposta está demorando mais que o esperado. Sua mensagem pode já ter sido enviada — confira antes de tentar de novo.'

export interface PendingTurn {
  localId: string
  text: string
  attachments: Attachment[]
  /** Client-side send time (epoch ms) — drives FIFO matching + the timeout. */
  sentAt: number
  status: 'sending' | 'error'
  errorMessage?: string
}

type Listener = () => void

const turnsByKey = new Map<string, PendingTurn[]>()
const listenersByKey = new Map<string, Set<Listener>>()

const EMPTY_TURNS: PendingTurn[] = []

function notify(key: string) {
  for (const listener of listenersByKey.get(key) ?? []) listener()
}

function setTurns(key: string, turns: PendingTurn[]) {
  turnsByKey.set(key, turns)
  notify(key)
}

/** Current pending turns for a conversation key (stable reference until changed). */
export function getPendingTurns(key: string): PendingTurn[] {
  return turnsByKey.get(key) ?? EMPTY_TURNS
}

/** Subscribes to changes for one conversation key. Returns the unsubscribe fn. */
export function subscribePendingTurns(key: string, listener: Listener): () => void {
  let set = listenersByKey.get(key)
  if (!set) {
    set = new Set()
    listenersByKey.set(key, set)
  }
  set.add(listener)
  return () => {
    set?.delete(listener)
  }
}

/** Registers a new optimistic turn; returns the generated local id. */
export function addPendingTurn(
  key: string,
  input: { text: string; attachments: Attachment[] },
): string {
  const localId = crypto.randomUUID()
  const turn: PendingTurn = {
    localId,
    text: input.text,
    attachments: input.attachments,
    sentAt: Date.now(),
    status: 'sending',
  }
  setTurns(key, [...getPendingTurns(key), turn])
  return localId
}

/** Re-queues an existing (failed) turn as 'sending', with a fresh sentAt. */
export function retryPendingTurn(key: string, localId: string): void {
  setTurns(
    key,
    getPendingTurns(key).map((t) =>
      t.localId === localId
        ? { ...t, status: 'sending', errorMessage: undefined, sentAt: Date.now() }
        : t,
    ),
  )
}

/** Marks a turn as failed (send it stays visible with a retry/discard action). */
export function setPendingTurnError(key: string, localId: string, message: string): void {
  setTurns(
    key,
    getPendingTurns(key).map((t) =>
      t.localId === localId ? { ...t, status: 'error', errorMessage: message } : t,
    ),
  )
}

/** Drops a turn (discarded by the user, or resolved by the real message arriving). */
export function removePendingTurn(key: string, localId: string): void {
  const current = getPendingTurns(key)
  const next = current.filter((t) => t.localId !== localId)
  if (next.length !== current.length) setTurns(key, next)
}

/** Moves all pending turns from one key to another (new conversation → its real id). */
export function movePendingTurns(fromKey: string, toKey: string): void {
  if (fromKey === toKey) return
  const moving = getPendingTurns(fromKey)
  if (moving.length === 0) return
  setTurns(fromKey, [])
  setTurns(toKey, [...getPendingTurns(toKey), ...moving])
}

/**
 * Drops every pending turn, across every conversation. Used when the signed-in
 * account changes (see `lib/auth.tsx`) — the store is module-level (survives
 * route remounts on purpose, see the file header), so without this an account
 * switch within the same tab would leak user A's in-flight turn to user B.
 */
export function clearAllOptimisticState(): void {
  const keys = new Set([...turnsByKey.keys(), ...listenersByKey.keys()])
  turnsByKey.clear()
  for (const key of keys) notify(key)
}

export type TurnPhase = 'sending' | 'error' | 'awaiting-reply' | 'resolved'

export interface ClassifiedTurn {
  turn: PendingTurn
  phase: TurnPhase
  /**
   * Only meaningful for `phase: 'error'`: a real message matching this turn's
   * text already landed (same predicate as the live-matching below) — the
   * send likely succeeded despite the client-perceived failure (e.g. a
   * network error after the write went through, or the defensive timeout
   * firing after all). The turn still never auto-resolves — the caller
   * should let the user discard it explicitly instead of offering "retry",
   * which would duplicate the message server-side (`sendMessage` isn't
   * idempotent).
   */
  likelySent: boolean
}

/** True when a real message is this turn's echo (same predicate used to advance the FIFO scan below). */
function matchesTurn(message: MessageDoc, turn: PendingTurn): boolean {
  return (
    message.role === 'user' &&
    message.content === turn.text &&
    message.createdAt >= turn.sentAt - CLOCK_SKEW_MS
  )
}

/**
 * Pure reconciliation: for each pending turn (oldest first), looks for the
 * first not-yet-claimed real user message with the same content created at
 * or after `sentAt` (minus clock skew). 'error' turns never auto-resolve —
 * a failed send must be retried or discarded explicitly, never silently
 * swapped for a coincidentally-matching later message.
 *
 * - no match found            → 'sending' (still shows the optimistic bubble)
 * - matched, no reply yet     → 'awaiting-reply' (real bubble is in the feed;
 *                                keep showing the thinking placeholder)
 * - matched, reply landed     → 'resolved' (caller should drop the turn)
 */
export function classifyPendingTurns(
  messages: MessageDoc[],
  turns: PendingTurn[],
): ClassifiedTurn[] {
  const ordered = [...turns].sort((a, b) => a.sentAt - b.sentAt)
  let searchFrom = 0
  const results: ClassifiedTurn[] = []

  for (const turn of ordered) {
    if (turn.status === 'error') {
      // Doesn't consume `searchFrom` — an errored turn never claims a real
      // message, so it can't steal the match a later 'sending' turn needs.
      const likelySent = messages.some((m) => matchesTurn(m, turn))
      results.push({ turn, phase: 'error', likelySent })
      continue
    }

    let matchedIndex = -1
    for (let i = searchFrom; i < messages.length; i++) {
      const m = messages[i]
      if (m && matchesTurn(m, turn)) {
        matchedIndex = i
        break
      }
    }

    if (matchedIndex === -1) {
      results.push({ turn, phase: 'sending', likelySent: false })
      continue
    }
    searchFrom = matchedIndex + 1

    const hasReplyAfter = messages.slice(matchedIndex + 1).some((m) => m.role !== 'user')
    results.push({ turn, phase: hasReplyAfter ? 'resolved' : 'awaiting-reply', likelySent: false })
  }

  return results
}

/**
 * Local ids of open turns ('sending' | 'awaiting-reply') that have been
 * waiting past `THINKING_TIMEOUT_MS` — pure (no timers/React) so the decision
 * is unit-testable on its own; the caller (`useOptimisticTurns`) applies the
 * actual `setPendingTurnError` mutation on an interval.
 */
export function findTimedOutTurns(classified: ClassifiedTurn[], now: number): string[] {
  return classified
    .filter(
      ({ phase, turn }) =>
        (phase === 'sending' || phase === 'awaiting-reply') &&
        now - turn.sentAt > THINKING_TIMEOUT_MS,
    )
    .map(({ turn }) => turn.localId)
}
